import {
  AfterViewInit,
  Component,
  DestroyRef,
  Input,
  OnInit,
  inject,
  output,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import {
  BehaviorSubject,
  Observable,
  catchError,
  delay,
  map,
  mergeMap,
  of,
  take,
} from 'rxjs';
import { LocationProfilePopupComponent } from 'src/app/components/admin/location-admin/location-profile-popup/location-profile-popup.component';
import { ProfileBundle } from 'src/app/components/admin/location-admin/location-profile-popup/location-profile.constants';
import { BANNER_TYPE } from 'src/app/components/banner/banner.constants';
import { SpecialCharacters } from 'src/app/core/enums';
import { AdminSearchCriteria } from 'src/app/core/enums/admin-search-criteria.enum';
import { UserAdmin } from 'src/app/core/models/admin';
import {
  Location,
  LocationProfile,
} from 'src/app/core/models/admin/location-admin.model';
import { Items } from 'src/app/core/models/common/items.model';
import { TableColumn } from 'src/app/core/models/common/table-column.model';
import { LocationService } from 'src/app/core/services/admin/location/location.service';
import { UserService } from 'src/app/core/services/user/user.service';
import { CommonUtils } from 'src/app/core/utils/common/common.utils';
import { RolePermissionsUtils } from 'src/app/core/utils/role-permissions/role-permissions.utils';
import { BUTTON_CLICK } from '../../modal/exit-modal/exit-modal.constants';
import { ERROR_TEXT, IMAGE_SRC, STATUS_OPTIONS } from '../../shared-constants';
import {
  CONSTS,
  ColumnIds,
  ColumnText,
  EXCEL_FILE_NAME,
  FilteredLocation,
  SETUP_DROPDOWN,
  SelectedLocationType,
} from './location-admin-table.constants';

@Component({
  selector: 'app-location-admin-table',
  templateUrl: './location-admin-table.component.html',
  styleUrls: ['./location-admin-table.component.scss'],
  standalone: false,
})
export class LocationAdminTableComponent implements OnInit, AfterViewInit {
  @Input() radioHeader!: string;
  @Input() firstOptionValue!: string;
  @Input() firstOptionName!: string;
  @Input() secondOptionValue!: string;
  @Input() secondOptionName!: string;
  @Input() headerText = CONSTS.WELCOME_TEXT;
  @Input() hasRadioButton = false;
  @Input() radioModel!: AdminSearchCriteria;
  @Input() isAdminSearch = false;
  radioButtonChange = output<Event>();

  readonly destroy = inject(DestroyRef);
  private userService = inject(UserService);

  readonly displayedColumns = Object.values(ColumnIds);
  readonly COLUMNTEXT = ColumnText;
  readonly STATUS_OPTIONS = STATUS_OPTIONS;
  readonly IMAGE_SRC = IMAGE_SRC;
  readonly ERROR_TEXT = ERROR_TEXT;
  readonly SELECT_ALL = STATUS_OPTIONS.ALL_SELECTED;
  readonly CONSTS = CONSTS;
  readonly setup = SETUP_DROPDOWN;
  readonly SPECIAL_CHARS = SpecialCharacters;
  readonly IMAGES = IMAGE_SRC;

  // Create admin table columns
  locationCodeData = this.commonUtils.createTableColumn(
    ColumnIds.LOCATION_CODE,
  );
  locationNameData = this.commonUtils.createTableColumn(
    ColumnIds.LOCATION_NAME,
  );
  packageSlicData = this.commonUtils.createTableColumn(ColumnIds.PACKAGE_SLIC);
  hubSlicData = this.commonUtils.createTableColumn(ColumnIds.HUB_SLIC);
  mnemonicData = this.commonUtils.createTableColumn(ColumnIds.MNEMONIC);
  locationGroupsData = this.commonUtils.createTableColumn(ColumnIds.GROUP);
  countryData = this.commonUtils.createTableColumn(ColumnIds.COUNTRY);
  regionData = this.commonUtils.createTableColumn(ColumnIds.REGION);
  districtData = this.commonUtils.createTableColumn(ColumnIds.DISTRICT);
  messagingData = this.commonUtils.createTableColumn(ColumnIds.MESSAGING);
  gioData = this.commonUtils.createTableColumn(ColumnIds.GIO);
  byodData = this.commonUtils.createTableColumn(ColumnIds.BYOD);
  idoData = this.commonUtils.createTableColumn(ColumnIds.IDO);
  apiKeyData = this.commonUtils.createTableColumn(ColumnIds.API_KEY);
  setupData = this.commonUtils.createTableColumn(ColumnIds.SETUP);

  removeLocation = false;
  removeLocationConfirm = false;
  selectedLocation!: SelectedLocationType;

  dataSource = new MatTableDataSource<Location>();
  removeUser = false;
  edit = false;
  currentUser!: UserAdmin | undefined;
  locationData: Location[] = [];
  resetSelectDropdowns = new BehaviorSubject<boolean>(false);
  dropdownOpen = [false, false, false, false, false, false];

  constructor(
    private readonly dialog: MatDialog,
    private readonly locationService: LocationService,
    public commonUtils: CommonUtils,
    private readonly roleUtils: RolePermissionsUtils,
  ) {}

  ngOnInit(): void {
    this.userService
      .getCurrentUser$()
      .pipe(
        takeUntilDestroyed(this.destroy),
        mergeMap((user) => {
          this.currentUser = user;
          this.clearAllFilters();
          return this.getLocationData(user?.locationGroup);
        }),
      )
      .subscribe();
  }

  ngAfterViewInit() {
    this.dataSource.filterPredicate = this.commonUtils.createFilter();
  }

  getLocationData(groupCode: string): Observable<boolean> {
    return this.locationService
      .getLocationsByGroup(
        this.isAdminSearch ? null : groupCode,
        this.isAdminSearch,
      )
      .pipe(
        take(1),
        map((locations) => {
          const locationData = locations?.response?.locations;
          locationData?.sort((a, b) =>
            a.locationCode.localeCompare(b.locationCode),
          );
          this.locationData = locationData || [];
          this.dataSource.data = this.locationData;
          return false;
        }),
      );
  }

  onRemoveLocation() {
    const tableData = this.dataSource.data;
    tableData.splice(this.selectedLocation?.index, 1);
    this.dataSource.data = [...tableData];
    this.removeLocationConfirm = false;
  }

  exportExcel(): void {
    this.commonUtils.exportExcel(
      Object.values(ColumnText),
      EXCEL_FILE_NAME,
      this.simplifyDataSource(),
    );
  }

  clearAllFilters() {
    this.commonUtils.clearAllFilters(
      this.dataSource,
      this.locationData,
      [
        this.locationCodeData.formControl,
        this.locationNameData.formControl,
        this.packageSlicData.formControl,
        this.hubSlicData.formControl,
        this.mnemonicData.formControl,
        this.locationGroupsData.formControl,
        this.countryData.formControl,
        this.regionData.formControl,
        this.districtData.formControl,
      ],
      [this.setupData],
    );
    this.resetSelectDropdowns.next(true);
  }

  search() {
    this.commonUtils.searchFilter(
      [
        this.locationCodeData,
        this.locationNameData,
        this.packageSlicData,
        this.hubSlicData,
        this.mnemonicData,
        this.locationGroupsData,
        this.countryData,
        this.regionData,
        this.districtData,
      ],
      this.dataSource,
      [
        this.setupData,
        this.messagingData,
        this.gioData,
        this.byodData,
        this.idoData,
        this.apiKeyData,
      ],
    );
  }

  onStatusChangeForDropdowns($event: unknown, field: TableColumn) {
    field.ids[0] = $event as Items;
    this.search();
  }

  addLocation() {
    this.edit = false;
    this.openProfile$({} as LocationProfile, false).subscribe();
  }

  editLocation(data: Location) {
    this.edit = true;
    if (data?.locationCode) {
      this.locationService
        .getLocationProfile(data.locationCode)
        .pipe(
          take(1),
          mergeMap((profile) => {
            return this.openProfile$(profile?.response, true);
          }),
          catchError(() => {
            return of(false);
          }),
        )
        .subscribe();
    }
  }

  openProfile$(profile: LocationProfile, edit?: boolean) {
    return this.dialog
      .open(LocationProfilePopupComponent, {
        disableClose: true,
        width: '57rem',
        maxHeight: '95vh',
        data: {
          profile: profile,
          edit: edit,
        },
      })
      .afterClosed()
      .pipe(
        takeUntilDestroyed(this.destroy),
        delay(100),
        mergeMap((data: ProfileBundle) => {
          return data
            ? this.addLocationCall(data)
            : this.getLocationData(this.currentUser?.locationGroup || '');
        }),
      );
  }

  removeLocationDialog(locationItem: Location, index: number) {
    this.selectedLocation = { ...locationItem, index };
    this.openRemoveLocationDialog();
  }

  openRemoveLocationDialog() {
    this.commonUtils.openWarningModal({
      header: this.CONSTS.REMOVE_LOCATION,
      body: this.CONSTS.CONFIRM_REMOVE,
      extraText: this.selectedLocation.locationName,
    });
  }

  openConfirmDialog() {
    this.commonUtils
      .openWarningModal({
        header: this.CONSTS.ALERT,
        body: this.CONSTS.CONFIRM_REMOVE,
        extraText: this.selectedLocation.locationName,
      })
      .afterClosed()
      .subscribe((resp) => {
        if (resp.button === BUTTON_CLICK.ACCEPT) {
          this.onRemoveLocation();
        } else {
          this.openRemoveLocationDialog();
        }
      });
  }

  emitRadioButtonChange($event: Event) {
    this.radioButtonChange.emit($event);
  }

  private addLocationCall(data: ProfileBundle) {
    return this.locationService
      .addLocation(data.profile, data.profile?.locationCode)
      .pipe(
        mergeMap((loc) => {
          if (loc?.code === 200 && loc?.response) {
            this.commonUtils.updateBannerType(BANNER_TYPE.API_SUCCESS);
            return this.getLocationData(this.currentUser?.locationGroup || '');
          }
          return of(false);
        }),
      );
  }

  private simplifyDataSource(): FilteredLocation[] {
    const temp: FilteredLocation[] = [];

    this.dataSource.filteredData.forEach((item) => {
      const locationGroups =
        (item as unknown as { locationGroups?: string | string[] })
          .locationGroups || '';

      temp.push({
        locationCode: item.locationCode.toString(),
        locationName: item.locationName,
        pkgSlic: item.pkgSlic,
        hubSlic: item.hubSlic,
        mnemonic: item.mnemonic,
        locationGroups: Array.isArray(locationGroups)
          ? locationGroups.toString()
          : locationGroups,
        country: item.country,
        region: item.region,
        district: item.district,
        locationSetup: item.locationSetup,
        messaging: item.messaging,
        gioEnabled: item.gioEnabled,
        byod: item.byod,
        ido: item.ido,
        apiKey: item.apiKey,
      });
    });

    return temp;
  }

  get canEditDeleteLocation() {
    const role = this.currentUser?.roleName ?? '';
    return this.roleUtils.canEditDeleteLocation(role);
  }

  get canAddLocations() {
    const role = this.currentUser?.roleName ?? '';
    return this.roleUtils.canAddLocationAdmin(role);
  }

  get isFilterEnabled() {
    return (
      this.locationCodeData.formControl.value !== '' ||
      this.locationNameData.formControl.value !== '' ||
      this.packageSlicData.formControl.value !== '' ||
      this.hubSlicData.formControl.value !== '' ||
      this.mnemonicData.formControl.value !== '' ||
      this.locationGroupsData.formControl.value !== '' ||
      this.countryData.formControl.value !== '' ||
      this.regionData.formControl.value !== '' ||
      this.districtData.formControl.value !== '' ||
      this.setupData.ids.length > 0 ||
      this.messagingData.ids.length > 0 ||
      this.gioData.ids.length > 0 ||
      this.byodData.ids.length > 0 ||
      this.idoData.ids.length > 0 ||
      this.apiKeyData.ids.length > 0
    );
  }
}
